import React, { Component } from "react";
import PropTypes from "prop-types";
import "../styles/ResearchSection.css";

class ResearchItem extends Component {
  state = {
    videoActive: false,
  };

  videoRef = React.createRef();

  isHovered = false;

  isFocused = false;

  startVideo = (interaction) => {
    if (interaction === "hover") {
      this.isHovered = true;
    } else {
      this.isFocused = true;
    }

    const video = this.videoRef.current;

    if (video) {
      video.play().catch(() => {
        // Keep the still image visible if the browser blocks playback.
      });
    }
  };

  stopVideo = (interaction) => {
    if (interaction === "hover") {
      this.isHovered = false;
    } else {
      this.isFocused = false;
    }

    if (this.isHovered || this.isFocused) {
      return;
    }

    const video = this.videoRef.current;

    this.setState({ videoActive: false });

    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  handleVideoPlay = () => {
    if (this.isHovered || this.isFocused) {
      this.setState({ videoActive: true });
    }
  };

  render() {
    const {
      title,
      image,
      video,
      authors,
      highlightedAuthor,
      journal,
      tag,
      description,
      projectUrl,
      codeUrl,
      arxivUrl,
      demoUrl,
      paperUrl,
      videoUrl,
      pressUrl,
      imageFit,
      highlighted,
    } = this.props;
    const primaryUrl = pressUrl || arxivUrl || projectUrl || codeUrl || videoUrl;
    const links = [
      projectUrl && { label: "Project Page", url: projectUrl },
      codeUrl && { label: "Code", url: codeUrl },
      arxivUrl && { label: "arXiv", url: arxivUrl },
      paperUrl && { label: "Paper", url: paperUrl },
      demoUrl && { label: "Demo", url: demoUrl },
      videoUrl && { label: "Video", url: videoUrl },
      pressUrl && { label: "Press", url: pressUrl },
    ].filter(Boolean);
    const openPrimaryUrl = (event) => {
      const clickedLink =
        event.target instanceof Element && event.target.closest("a");

      if (!primaryUrl || clickedLink) {
        return;
      }

      window.open(primaryUrl, "_blank", "noopener,noreferrer");
    };
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        openPrimaryUrl(event);
      }
    };
    const thumbnail = image && (
      <span
        className={`thumbnail-media ${
          video && this.state.videoActive ? "video-active" : ""
        }`}
        onMouseEnter={() => this.startVideo("hover")}
        onMouseLeave={() => this.stopVideo("hover")}
        onFocus={() => this.startVideo("focus")}
        onBlur={() => this.stopVideo("focus")}
      >
        <img
          src={image}
          alt={title}
          className="thumbnail"
          style={imageFit ? { objectFit: imageFit } : undefined}
        />
        {video && (
          <video
            ref={this.videoRef}
            src={video}
            className="thumbnail thumbnail-video"
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            onPlay={this.handleVideoPlay}
          />
        )}
      </span>
    );

    return (
      <div
        className={`research-item ${highlighted ? "highlighted" : ""}`}
        onClick={openPrimaryUrl}
        onKeyDown={handleKeyDown}
        role={primaryUrl ? "link" : undefined}
        tabIndex={primaryUrl ? 0 : undefined}
      >
        {image &&
          (primaryUrl ? (
            <a
              href={primaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="thumbnail-link"
            >
              {thumbnail}
            </a>
          ) : (
            thumbnail
          ))}
        <div className="content">
          <h3>
            {primaryUrl ? (
              <a href={primaryUrl} target="_blank" rel="noopener noreferrer">
                {title}
              </a>
            ) : (
              title
            )}
          </h3>
          <p className="authors">
            {authors
              .map((author, index) => {
                const authorName =
                  typeof author === "string" ? author : author.name;
                const authorUrl = typeof author === "string" ? null : author.url;
                const authorStyle = {
                  textDecoration:
                    index === highlightedAuthor ? "underline" : "none",
                  color: "inherit",
                };

                return (
                  <span key={`${authorName}-${index}`}>
                    {authorUrl ? (
                      <a
                        href={authorUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={authorStyle}
                      >
                        {authorName}
                      </a>
                    ) : (
                      <span style={authorStyle}>{authorName}</span>
                    )}
                  </span>
                );
              })
              .reduce((prev, curr) => [prev, ", ", curr])}
          </p>
          {(journal || tag) && (
            <div className="research-meta">
              {journal && <p className="journal">{journal}</p>}
              {tag && <span className="research-tag">{tag}</span>}
            </div>
          )}
          {description && <p className="description">{description}</p>}
          <ul className="links">
            {links.map((link, index) => (
              <React.Fragment key={link.label}>
                <li>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </a>
                </li>
                {index < links.length - 1 && <li>/</li>}
              </React.Fragment>
            ))}
          </ul>
        </div>
      </div>
    );
  }
}

ResearchItem.propTypes = {
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  video: PropTypes.string,
  authors: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string,
      }),
    ])
  ).isRequired,
  highlightedAuthor: PropTypes.number,
  journal: PropTypes.string,
  tag: PropTypes.string,
  description: PropTypes.string,
  projectUrl: PropTypes.string,
  codeUrl: PropTypes.string,
  arxivUrl: PropTypes.string,
  demoUrl: PropTypes.string,
  paperUrl: PropTypes.string,
  videoUrl: PropTypes.string,
  pressUrl: PropTypes.string,
  imageFit: PropTypes.string,
  highlighted: PropTypes.bool,
};

export default ResearchItem;
